import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import moment from "moment";

import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries} from "@khan/shared-styles";
import {cssUrl} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";

import {withStrings} from "./locales/accessibility-statement-page/load-strings.tsx";

function AccessibilityStatementPage() {
    return (
        <PageRoot settings={{title: t`Accessibility Statement`}}>
            <div className={css(styles.wrapper)}>
                <div className={css(styles.header)}>
                    <div className={css(styles.headerCopy)}>
                        <h1 className={css(styles.pageTitle)}>
                            <Trans>Khan Academy Accessibility Statement</Trans>
                        </h1>
                        <p className={css(styles.lastUpdated)}>
                            <Trans>
                                Last Updated:{" "}
                                {moment("2024-12-06", "YYYY-MM-DD").format(
                                    "LL",
                                )}
                            </Trans>
                        </p>
                    </div>
                </div>
                <div className={css(styles.body)}>
                    <div className={css(styles.columns)}>
                        <div className={css(styles.callOutSection)}>
                            <p className={css(styles.callOutParagraph)}>
                                <Trans>
                                    At Khan Academy accessibility is a direct
                                    extension of our mission to provide a free,
                                    world-class education to anyone, anywhere.
                                    We are committed to making our products
                                    available to learners across the wide
                                    spectrum of ability.
                                </Trans>
                            </p>
                        </div>
                        <div className={css(styles.contentColumn)}>
                            <h2 className={css(styles.sectionTitle)}>
                                <Trans>Accessibility Implementation</Trans>
                            </h2>
                            <p className={css(styles.paragraph)}>
                                <Trans>
                                    We continue our journey to increase the
                                    accessibility of our digital materials and
                                    products by collaborating with an external
                                    partner and implementing tactical measures
                                    to include accessibility throughout our
                                    processes and procedures. As a commitment to
                                    this work, we have adopted the Web Content
                                    Accessibility Guidelines (WCAG) 2.1 Level AA
                                    as our baseline to guide us in our
                                    development to deliver accessibility
                                    materials. We proactively search ways to
                                    incorporate accessibility and are committed
                                    to continuous improvement. Here are some
                                    current activities:
                                </Trans>
                            </p>
                            <ul className={css(styles.list)}>
                                <li>
                                    <Trans>
                                        Partnering with accessibility experts on
                                        strategy, approach, and continuous
                                        improvement
                                    </Trans>
                                </li>
                                <li>
                                    <Trans>
                                        Prioritizing critical accessibility
                                        fixes to our digital materials
                                    </Trans>
                                </li>
                                <li>
                                    <Trans>
                                        Incorporating access practices into our
                                        processes through analysis, tools, and
                                        training
                                    </Trans>
                                </li>
                            </ul>
                            <h2 className={css(styles.sectionTitle)}>
                                <Trans>Seek User Feedback</Trans>
                            </h2>
                            <p className={css(styles.paragraph)}>
                                <Trans>
                                    We welcome feedback on the accessibility of
                                    Khan Academy. Simply put, our efforts cannot
                                    succeed without it. Please let us know if
                                    you encounter accessibility barriers or have
                                    suggestions for improving the accessibility
                                    of our platform.
                                </Trans>
                            </p>
                            <ul className={css(styles.list)}>
                                <li>
                                    <Trans>
                                        Web:{" "}
                                        <Link href="https://support.khanacademy.org/hc/requests/new">
                                            Submit Feedback
                                        </Link>
                                    </Trans>
                                </li>
                                <li>
                                    <Trans>
                                        Email:{" "}
                                        <Link href="mailto:accessibility@khanacademy.org">
                                            accessibility@khanacademy.org
                                        </Link>
                                    </Trans>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </PageRoot>
    );
}

export default withStrings()(AccessibilityStatementPage);

const styles = StyleSheet.create({
    wrapper: {
        minHeight: "100vh",
        fontSize: 16,
        [mediaQueries.smOrSmaller]: {
            fontSize: 14,
        },
    },
    header: {
        height: 300,
        width: "100vw",
        display: "flex",
        backgroundPosition: "center",
        backgroundImage: cssUrl("/images/cookie_notice_header_desktop.jpg"),
        alignItems: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        [mediaQueries.smOrSmaller]: {
            height: 0,
            paddingTop: "75%",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            width: "100vw",
            backgroundImage: cssUrl("/images/cookie_notice_header_mobile.jpg"),
        },
    },
    headerCopy: {
        display: "flex",
        flexDirection: "column",
        maxWidth: "55%",
        marginLeft: "5%",
        [mediaQueries.smOrSmaller]: {
            marginTop: "-70%",
            maxWidth: "60%",
            marginLeft: "20%",
            justifyContent: "center",
        },
    },
    pageTitle: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: 42,
        fontWeight: 600,
        color: color.darkBlue,
        margin: 0,
        [mediaQueries.smOrSmaller]: {
            fontSize: 35,
            textAlign: "center",
        },
    },
    lastUpdated: {
        fontSize: 18,
        fontStyle: "italic",
        fontWeight: 700,
        lineHeight: "30px",
        color: color.darkBlue,
        margin: 0,
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    list: {
        listStyle: "disc",
        marginLeft: 20,
        lineHeight: "32px",
        fontSize: 18,
        marginBottom: 20,
    },
    body: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
        padding: "40px 0",
    },
    paragraph: {
        fontSize: 18,
        lineHeight: "32px",
    },
    sectionTitle: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: 34,
        fontWeight: "normal",
        margin: 0,
        [mediaQueries.smOrSmaller]: {
            fontSize: 24,
        },
    },
    columns: {
        display: "flex",
        maxWidth: "1200px",
        fontSize: 16,
        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
            fontSize: 14,
        },
    },
    callOutSection: {
        display: "block",
        fontSize: 18,
        fontWeight: 700,
        marginRight: 25,
        backgroundColor: "#F4EEE2",
        [mediaQueries.smOrSmaller]: {
            margin: "0 4%",
            fontSize: 14,
        },
        [mediaQueries.mdOrLarger]: {
            maxHeight: 380,
            display: "flex",
            flex: 1,
            flexDirection: "column",
        },
    },
    callOutParagraph: {
        backgroundColor: "#F4EEE2",
        padding: "8%",
        fontSize: 18,
        lineHeight: "32px",
    },
    contentColumn: {
        flex: 2,
        display: "flex",
        flexDirection: "column",
        [mediaQueries.smOrSmaller]: {
            padding: "5%",
        },
    },
});
